import { FeedbackLine } from "../ui/FeedbackLine";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Box,
  Center,
  CloseButton,
  Group,
  Modal,
  MultiSelect,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconEdit, IconNews, IconPlus, IconTrash } from "@tabler/icons-react";
import {
  useAdminNewsList,
  useAdminNewsTags,
  useCreateNews,
  useCreateNewsTag,
  useDeleteNews,
  useUpdateNews,
} from "../api/queries/news";
import type { NewsItem, NewsUpsertPayload } from "../api/types/news";
import { TagBadge } from "../news/NewsPage";
import { formatDate } from "../time/format";
import { useZone } from "../time/useZone";
import { Chip } from "../ui/Chip";
import { confirm } from "../ui/confirm";
import { MarkdownContent } from "../news/MarkdownContent";
import { ApiError } from "../api/client";

const PAGE_SIZE = 20;

export function NewsAdminPage() {
  const { t } = useTranslation("admin");
  const zone = useZone();

  const [page, setPage] = useState(1);
  const [editingItem, setEditingItem] = useState<NewsItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const offset = (page - 1) * PAGE_SIZE;
  const { data, isLoading, isError } = useAdminNewsList({
    offset,
    limit: PAGE_SIZE,
  });

  const updateNews = useUpdateNews();
  const deleteNews = useDeleteNews();

  const handleOpenCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
    setActionError(null);
  };

  const handleOpenEdit = (item: NewsItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
    setActionError(null);
  };

  const handleTogglePublish = async (item: NewsItem) => {
    setActionError(null);
    try {
      await updateNews.mutateAsync({
        id: item.id,
        body: {
          title: item.title,
          body: item.body,
          tag_ids: item.tags.map((t) => t.id),
          published: !item.published,
        },
      });
    } catch (err: unknown) {
      setActionError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  const handleDelete = async (item: NewsItem) => {
    setActionError(null);
    const ok = await confirm(
      t("newsAdmin.deleteConfirm", { title: item.title }),
      t("newsAdmin.deleteAction"),
      true,
    );
    if (!ok) return;

    try {
      await deleteNews.mutateAsync(item.id);
    } catch (err: unknown) {
      setActionError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  return (
    <Paper p="md" m={20} style={{ overflowY: "auto", minHeight: 0 }}>
      <Group justify="space-between" align="center" mb="lg" wrap="wrap" gap="md">
        <Group gap="xs" align="center">
          <IconNews size={24} stroke={1.5} aria-hidden="true" />
          <Title order={2} fz="lg" lh={1.5}>
            {t("newsAdmin.heading")}
          </Title>
        </Group>

        <Chip variant="accent" onClick={handleOpenCreate} leftSection={<IconPlus size={16} />}>
          {t("newsAdmin.createButton")}
        </Chip>
      </Group>

      <FeedbackLine role="alert" mb="md">
        {actionError && (
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Text inherit flex={1} miw={0}>
              {actionError}
            </Text>
            <CloseButton
              size={18}
              aria-label={t("common.close", { ns: "common" })}
              onClick={() => setActionError(null)}
            />
          </Group>
        )}
      </FeedbackLine>

      {isLoading && (
        <Stack gap="sm">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={40} />
          ))}
        </Stack>
      )}

      {isError && (
        <Alert color="red" variant="light" my="md">
          {t("newsAdmin.errorLoading")}
        </Alert>
      )}

      {!isLoading && !isError && data && data.items.length === 0 && (
        <Paper p="xl" withBorder style={{ textAlign: "center" }} my="md">
          <Text c="dimmed">{t("newsAdmin.empty")}</Text>
        </Paper>
      )}

      {!isLoading && !isError && data && data.items.length > 0 && (
        <Box style={{ overflowX: "auto" }}>
          <Table miw={800} verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("newsAdmin.colTitle")}</Table.Th>
                <Table.Th w={180}>{t("newsAdmin.colTags")}</Table.Th>
                <Table.Th w={120}>{t("newsAdmin.colStatus")}</Table.Th>
                <Table.Th w={130}>{t("newsAdmin.colVersion")}</Table.Th>
                <Table.Th w={160}>{t("newsAdmin.colUpdated")}</Table.Th>
                <Table.Th w={260}>{t("newsAdmin.colActions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.items.map((item) => {
                const updatedMs = Date.parse(item.updated_at);
                const dateStr = Number.isNaN(updatedMs) ? "" : formatDate(updatedMs, zone.name);
                return (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Text fw={500} lineClamp={1} title={item.title}>
                        {item.title}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        {item.tags.map((tag) => (
                          <TagBadge key={tag.id} tag={tag} />
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={item.published ? "green" : "gray"}
                        variant={item.published ? "filled" : "light"}
                        size="sm"
                      >
                        {item.published
                          ? t("newsAdmin.statusPublished")
                          : t("newsAdmin.statusDraft")}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {item.release_version ? (
                        <Badge variant="outline" size="sm">
                          {item.release_version}
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {dateStr}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Chip
                          onClick={() => handleOpenEdit(item)}
                          leftSection={<IconEdit size={14} />}
                        >
                          {t("newsAdmin.actionEdit")}
                        </Chip>
                        <Chip
                          variant={item.published ? undefined : "success"}
                          onClick={() => handleTogglePublish(item)}
                          disabled={updateNews.isPending}
                        >
                          {item.published
                            ? t("newsAdmin.actionUnpublish")
                            : t("newsAdmin.actionPublish")}
                        </Chip>
                        <Chip
                          variant="danger"
                          onClick={() => handleDelete(item)}
                          disabled={deleteNews.isPending}
                          aria-label={t("newsAdmin.actionDelete")}
                        >
                          <IconTrash size={14} />
                        </Chip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      {data && data.total > PAGE_SIZE && (
        <Center mt="xl">
          <Pagination
            total={Math.ceil(data.total / PAGE_SIZE)}
            value={page}
            onChange={setPage}
            size="sm"
          />
        </Center>
      )}

      {isModalOpen && (
        <NewsEditorModal
          key={editingItem ? `edit-${editingItem.id}` : "create"}
          item={editingItem}
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </Paper>
  );
}

function NewsEditorModal({
  item,
  opened,
  onClose,
}: {
  item: NewsItem | null;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("admin");
  const createNews = useCreateNews();
  const updateNews = useUpdateNews();
  const [error, setError] = useState<string | null>(null);

  const isNew = item === null;
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const isPending = createNews.isPending || updateNews.isPending || isCreatingTag;

  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(item?.tags.map((t) => t.id) ?? []);
  const form = useForm<{ title: string; published: boolean; body: string }>({
    mode: "uncontrolled",
    initialValues: {
      title: item?.title ?? "",
      published: item?.published ?? false,
      body: item?.body ?? "",
    },
    validate: {
      title: (val) => {
        const trimmed = val.trim();
        if (!trimmed) return t("newsAdmin.validationTitleRequired");
        if (trimmed.length > 200) return t("newsAdmin.validationTitleTooLong");
        return null;
      },
      body: (val) => {
        const trimmed = val.trim();
        if (!trimmed) return t("newsAdmin.validationBodyRequired");
        if (new TextEncoder().encode(trimmed).length > 100000) {
          return t("newsAdmin.validationBodyTooLong");
        }
        return null;
      },
    },
  });

  const [bodyPreview, setBodyPreview] = useState(form.getValues().body);

  const handleSubmit = async (values: { title: string; published: boolean; body: string }) => {
    if (isPending) return;
    setError(null);
    const payload: NewsUpsertPayload = {
      title: values.title,
      body: values.body,
      published: values.published,
      tag_ids: selectedTagIds,
    };
    try {
      if (isNew) {
        await createNews.mutateAsync(payload);
      } else {
        await updateNews.mutateAsync({ id: item.id, body: payload });
      }
      onClose();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Title order={3} fz="md">
          {isNew ? t("newsAdmin.modalTitleCreate") : t("newsAdmin.modalTitleEdit")}
        </Title>
      }
      size="xl"
      closeOnClickOutside={!isPending}
    >
      <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <FeedbackLine role="alert">{error}</FeedbackLine>

          <TextInput
            label={t("newsAdmin.inputTitle")}
            placeholder={t("newsAdmin.inputTitlePlaceholder")}
            maxLength={200}
            required
            key={form.key("title")}
            {...form.getInputProps("title")}
          />

          <TagSelect
            selectedIds={selectedTagIds}
            onChange={setSelectedTagIds}
            disabled={isPending}
            onCreatingChange={setIsCreatingTag}
          />

          <Switch
            label={t("newsAdmin.inputPublished")}
            description={t("newsAdmin.inputPublishedHint")}
            key={form.key("published")}
            {...form.getInputProps("published", { type: "checkbox" })}
          />

          <Box>
            <Text size="sm" fw={500} mb={6}>
              {t("newsAdmin.inputBody")}
            </Text>
            <Tabs
              defaultValue="write"
              onChange={(val) => {
                if (val === "preview") {
                  setBodyPreview(form.getValues().body);
                }
              }}
            >
              <Tabs.List mb="xs">
                <Tabs.Tab value="write">{t("newsAdmin.tabWrite")}</Tabs.Tab>
                <Tabs.Tab value="preview">{t("newsAdmin.tabPreview")}</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="write">
                <Textarea
                  placeholder={t("newsAdmin.inputBodyPlaceholder")}
                  minRows={10}
                  autosize
                  maxRows={24}
                  required
                  key={form.key("body")}
                  {...form.getInputProps("body")}
                />
              </Tabs.Panel>

              <Tabs.Panel value="preview">
                <Paper
                  p="md"
                  withBorder
                  radius="sm"
                  style={{ minHeight: 250, maxHeight: 500, overflowY: "auto" }}
                >
                  {bodyPreview.trim() ? (
                    <MarkdownContent markdown={bodyPreview} />
                  ) : (
                    <Text c="dimmed" fs="italic">
                      {t("newsAdmin.previewEmpty")}
                    </Text>
                  )}
                </Paper>
              </Tabs.Panel>
            </Tabs>
          </Box>

          <Group justify="flex-end" gap="sm" mt="md">
            <Chip onClick={onClose} disabled={isPending}>
              {t("common.cancel")}
            </Chip>
            <Chip type="submit" variant="accent" disabled={isPending}>
              {isPending
                ? t("common.saving")
                : isNew
                  ? t("newsAdmin.submitCreate")
                  : t("common.save")}
            </Chip>
          </Group>
        </Stack>
      </Box>
    </Modal>
  );
}

function TagSelect({
  selectedIds,
  onChange,
  disabled,
  onCreatingChange,
}: {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  onCreatingChange?: (creating: boolean) => void;
}) {
  const { t } = useTranslation("admin");
  const { data: allTags, isLoading, isError } = useAdminNewsTags();
  const createTag = useCreateNewsTag();
  const [newTagName, setNewTagName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const tags = allTags ?? [];
  const multiSelectData = tags.map((tag) => ({
    value: String(tag.id),
    label: tag.name,
  }));

  const selectedValues = selectedIds.map(String);

  const handleMultiSelectChange = (values: string[]) => {
    const nextIds = values.map((v) => parseInt(v, 10)).filter((id) => !Number.isNaN(id));
    onChange(nextIds);
  };

  const handleCreate = async () => {
    if (disabled || createTag.isPending) return;
    const trimmed = newTagName.trim();
    const charCount = [...trimmed].length;
    if (charCount < 1 || charCount > 50) {
      setCreateError(t("newsAdmin.validationTagNameLength"));
      return;
    }
    setCreateError(null);
    onCreatingChange?.(true);
    try {
      const created = await createTag.mutateAsync({ name: trimmed });
      onChange([...new Set([...selectedIds, created.id])]);
      setNewTagName("");
    } catch (err: unknown) {
      setCreateError(err instanceof ApiError ? err.detail : t("newsAdmin.tagCreateFailed"));
    } finally {
      onCreatingChange?.(false);
    }
  };

  return (
    <Box>
      <FeedbackLine role="alert" mb="xs">
        {isError && t("newsAdmin.errorLoadingTags")}
      </FeedbackLine>

      <MultiSelect
        label={t("newsAdmin.inputTags")}
        placeholder={selectedValues.length === 0 ? t("newsAdmin.inputTagsPlaceholder") : ""}
        data={multiSelectData}
        value={selectedValues}
        onChange={handleMultiSelectChange}
        searchable
        clearable
        disabled={disabled || isLoading}
      />

      <Group gap="xs" align="flex-end" mt="xs">
        <TextInput
          label={t("newsAdmin.tagCreate")}
          aria-label={t("newsAdmin.tagCreate")}
          placeholder={t("newsAdmin.tagCreatePlaceholder")}
          value={newTagName}
          disabled={disabled || createTag.isPending}
          style={{ flex: 1 }}
          onChange={(e) => setNewTagName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleCreate();
            }
          }}
        />
        <Chip
          type="button"
          variant="accent"
          disabled={disabled || createTag.isPending || [...newTagName.trim()].length === 0}
          onClick={() => {
            void handleCreate();
          }}
        >
          {createTag.isPending ? t("common.saving") : t("newsAdmin.tagCreateButton")}
        </Chip>
      </Group>

      <FeedbackLine role="alert" mt="xs">
        {createError}
      </FeedbackLine>
    </Box>
  );
}
